import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════════════
   FirstLookCelebration
   ───────────────────────────────────────────────────────────────────
   The post-signup "this is mine" moment.

   When a visitor dropped a URL into the Launch Kit teaser on the
   landing hero, sessionStorage carried that URL through the auth gate.
   The OnboardingWizard set `localStorage['scl-show-mini-preview']='1'`
   on completion. The first time this user lands on the dashboard, we
   show the same three tiles they saw blurred — now unblurred, with
   their real brand data — and let them tap a tile to go build the
   real version for the first time.
   ═══════════════════════════════════════════════════════════════════ */

const LIME = '#c8f135';
const BG = '#04050a';
const BORDER = 'rgba(255,255,255,0.07)';
const MONO = 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, "Cascadia Mono", Consolas, monospace';
const DISP = "'Syne', system-ui, sans-serif";

// Deterministic taglines/palette (same hashing as the teaser)
const PALETTES = [
  ['#ffb347', '#ff5e62'], ['#7be0ad', '#3aafa9'], ['#f9a8d4', '#a78bfa'],
  ['#c8f135', '#0fa3b1'], ['#fcd34d', '#fb923c'], ['#60a5fa', '#a78bfa'],
  ['#f87171', '#fbbf24'], ['#34d399', '#06b6d4'],
];
const TAGLINES = [
  ['Hand-crafted.', 'Always premium.'], ['Made for you.', 'Built to last.'],
  ['Effortless beauty.', 'Quiet luxury.'], ['Bold by design.', 'Felt by everyone.'],
  ['Less, but better.', 'Every detail.'], ['Modern essentials.', 'Timeless feel.'],
];
const hashStr = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return Math.abs(h); };

/* ────────── reusable tile shapes (no lock overlay) ────────── */

const TileLabel = ({ children, kicker }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
    <p style={{ fontFamily: MONO, fontSize: 10, letterSpacing: '0.22em', color: 'rgba(240,240,232,0.55)', textTransform: 'uppercase' }}>{children}</p>
    {kicker && <span style={{ fontFamily: MONO, fontSize: 9, color: LIME, letterSpacing: '0.14em', textTransform: 'uppercase' }}>{kicker}</span>}
  </div>
);

const AdTile = ({ preview }) => {
  const [c1, c2] = preview.palette;
  return (
    <div style={{ width: '100%' }}>
      <TileLabel kicker="3D Visual Ad">For your feed</TileLabel>
      <div style={{
        position: 'relative', width: '100%', aspectRatio: '1 / 1',
        borderRadius: 16, overflow: 'hidden', border: `1px solid ${BORDER}`,
        background: `radial-gradient(circle at 30% 30%, ${c1}, ${c2} 65%, #0c0f08 100%)`,
        boxShadow: '0 30px 60px -20px rgba(0,0,0,0.5)',
      }}>
        <div style={{
          position: 'absolute', top: '32%', left: '34%', width: '36%', aspectRatio: '1/1',
          borderRadius: '50%',
          background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.65), ${c1} 60%, ${c2})`,
          boxShadow: 'inset -10px -14px 30px rgba(0,0,0,0.35), 0 14px 28px rgba(0,0,0,0.3)',
        }} />
        {preview.logoUrl && (
          <img src={preview.logoUrl} alt={`${preview.brand} logo`} crossOrigin="anonymous" referrerPolicy="no-referrer" loading="lazy"
            style={{
              position: 'absolute', top: 16, left: 16, width: 36, height: 36,
              objectFit: 'contain', background: 'rgba(255,255,255,0.92)', borderRadius: 8, padding: 4,
              boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        )}
        <div style={{
          position: 'absolute', left: 16, right: 16, bottom: 16,
          fontFamily: DISP, fontWeight: 800, color: BG,
          background: 'rgba(255,255,255,0.92)',
          padding: '10px 14px', borderRadius: 8,
          fontSize: 'clamp(13px, 1.6vw, 17px)', letterSpacing: '-0.01em',
        }}>{preview.brand}: {preview.headline}</div>
      </div>
    </div>
  );
};

const CarouselTile = ({ preview }) => {
  const [c1, c2] = preview.palette;
  return (
    <div style={{ width: '100%' }}>
      <TileLabel kicker="3-Post Carousel">For your reach</TileLabel>
      <div style={{ width: '100%', borderRadius: 16, overflow: 'hidden', border: `1px solid ${BORDER}`, background: 'rgba(255,255,255,0.02)', padding: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{
              position: 'relative', aspectRatio: '1 / 1', borderRadius: 10, overflow: 'hidden',
              background:
                i === 0 ? `linear-gradient(135deg, ${c1}, ${c2})` :
                i === 1 ? `linear-gradient(135deg, #1a1d22, #0c0f08)` :
                          `linear-gradient(135deg, ${c2}, #0c0f08)`,
            }}>
              <div style={{
                position: 'absolute', left: 10, right: 10, top: '38%',
                fontFamily: DISP, fontWeight: 700, fontSize: 11, color: i === 1 ? c1 : BG,
                background: i === 1 ? 'transparent' : 'rgba(255,255,255,0.85)',
                padding: '4px 6px', borderRadius: 4, textAlign: 'center', lineHeight: 1.15,
              }}>{preview.carouselHeads[i]}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LandingTile = ({ preview }) => {
  const [c1, c2] = preview.palette;
  return (
    <div style={{ width: '100%' }}>
      <TileLabel kicker="Landing Page">For your launch</TileLabel>
      <div style={{
        width: '100%', borderRadius: 16, overflow: 'hidden', border: `1px solid ${BORDER}`,
        background: '#0c0f08', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.5)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 10px', borderBottom: `1px solid ${BORDER}`, background: 'rgba(0,0,0,0.5)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ff5f57' }} />
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#febc2e' }} />
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#28c840' }} />
          <span style={{ marginLeft: 10, fontFamily: MONO, fontSize: 9, color: 'rgba(240,240,232,0.4)' }}>{preview.host || preview.brand.toLowerCase() + '.com'}</span>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{
            position: 'relative', height: 110, borderRadius: 10, overflow: 'hidden',
            background: `linear-gradient(135deg, ${c1}, ${c2})`,
            marginBottom: 12,
          }}>
            {preview.ogImage && (
              <img src={preview.ogImage} alt={`${preview.brand} hero`} crossOrigin="anonymous" referrerPolicy="no-referrer" loading="lazy"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            )}
            <div style={{
              position: 'absolute', inset: 0,
              background: preview.ogImage ? 'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.55) 100%)' : 'transparent',
            }} />
            <div style={{
              position: 'absolute', left: 12, top: 14,
              fontFamily: DISP, fontWeight: 800, fontSize: 18,
              color: preview.ogImage ? '#fff' : BG,
              textShadow: preview.ogImage ? '0 1px 8px rgba(0,0,0,0.4)' : 'none',
            }}>{preview.brand}</div>
            <div style={{
              position: 'absolute', left: 12, bottom: 12,
              fontFamily: MONO, fontSize: 11,
              color: preview.ogImage ? 'rgba(255,255,255,0.85)' : 'rgba(4,5,10,0.7)',
            }}>{preview.subhead}</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{ height: 46, borderRadius: 7, background: 'rgba(255,255,255,0.04)', border: `1px solid ${BORDER}` }}>
                <div style={{ height: 6, width: '60%', margin: '10px 0 0 10px', borderRadius: 2, background: 'rgba(255,255,255,0.12)' }} />
                <div style={{ height: 5, width: '40%', margin: '6px 0 0 10px', borderRadius: 2, background: 'rgba(255,255,255,0.06)' }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 7, background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}` }}>
            <div style={{ height: 6, width: '40%', borderRadius: 2, background: 'rgba(255,255,255,0.12)' }} />
            <div style={{ height: 18, width: 64, borderRadius: 4, background: c1 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ────────── Confetti — CSS-only celebratory dots ────────── */
const Confetti = () => (
  <>
    <style>{`
      @keyframes scl-confetti-fall { 0% { transform: translateY(-20vh) rotate(0deg); opacity: 1; } 100% { transform: translateY(120vh) rotate(720deg); opacity: 0; } }
      .scl-conf { position: absolute; width: 8px; height: 12px; will-change: transform; pointer-events: none; }
    `}</style>
    {Array.from({ length: 36 }).map((_, i) => {
      const colors = [LIME, '#fcd34d', '#60a5fa', '#f87171', '#34d399'];
      const left = (i * 2.7 + Math.random() * 1.5) % 100;
      const delay = (i % 12) * 0.18;
      const duration = 3 + (i % 4) * 0.4;
      const color = colors[i % colors.length];
      return (
        <span key={i} className="scl-conf"
          style={{ left: `${left}%`, background: color, animation: `scl-confetti-fall ${duration}s ${delay}s cubic-bezier(0.4, 0, 0.6, 1) forwards` }} />
      );
    })}
  </>
);

/* ────────── deterministic fallback preview (no server scan available) ────────── */
const buildFallback = (url, brand) => {
  if (!url) return null;
  const clean = url.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0];
  const host = clean;
  const niceBrand = brand || clean.split('.')[0].replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const h = hashStr(host);
  const palette = PALETTES[h % PALETTES.length];
  const [t1, t2] = TAGLINES[h % TAGLINES.length];
  return {
    brand: niceBrand, host, palette,
    headline: t1, subhead: t2,
    carouselHeads: [`${niceBrand} — ${t1}`, `Why ${niceBrand}?`, 'Get yours →'],
    logoUrl: null, ogImage: null,
  };
};

const mergeLive = (fallback, live) => {
  if (!fallback) return null;
  if (!live) return fallback;
  return {
    ...fallback,
    brand: live.brand || fallback.brand,
    host: live.host || fallback.host,
    palette: (live.palette && live.palette.length === 2) ? live.palette : fallback.palette,
    carouselHeads: [
      `${live.brand || fallback.brand} — ${fallback.headline}`,
      `Why ${live.brand || fallback.brand}?`,
      'Get yours →',
    ],
    logoUrl: live.logo_url || null,
    ogImage: live.og_image || null,
  };
};

/* ────────── Component ────────── */
const FirstLookCelebration = ({ onDismiss }) => {
  const navigate = useNavigate();
  const [livePreview, setLivePreview] = useState(null);
  const [intelligence, setIntelligence] = useState(null);
  const [intelLoading, setIntelLoading] = useState(true);

  const { url, brand } = useMemo(() => {
    try {
      return {
        url: sessionStorage.getItem('scl_launch_kit_url') || '',
        brand: sessionStorage.getItem('scl_launch_kit_brand') || '',
      };
    } catch { return { url: '', brand: '' }; }
  }, []);

  const fallback = useMemo(() => buildFallback(url, brand), [url, brand]);
  const preview = useMemo(() => mergeLive(fallback, livePreview), [fallback, livePreview]);

  // Re-fetch live brand data so the celebration carries logo + og_image even
  // if the user opened the tab fresh after onboarding
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/launch-kit/scan`, { url }, { timeout: 6000 });
        if (!cancelled && res?.data?.ok) setLivePreview(res.data);
      } catch { /* fallback is already rendered */ }
    })();
    return () => { cancelled = true; };
  }, [url]);

  // Brand Intelligence — kicked off after we have the scan data so we can
  // pass title/description/palette to Claude. Slow (~5–30s); we show a
  // dedicated 'analyzing' state until it lands.
  useEffect(() => {
    if (!url) { setIntelLoading(false); return; }
    let cancelled = false;
    const payload = {
      url,
      brand: livePreview?.brand || brand || undefined,
      title: livePreview?.headline || undefined,
      description: livePreview?.description || undefined,
      palette: livePreview?.palette || undefined,
      og_image: livePreview?.og_image || undefined,
    };
    setIntelLoading(true);
    (async () => {
      try {
        const res = await axios.post(`${API_URL}/api/launch-kit/intelligence`, payload, { timeout: 45000 });
        if (!cancelled && res?.data?.ok) setIntelligence(res.data.intelligence);
      } catch { /* keep silent; tiles still render */ }
      finally { if (!cancelled) setIntelLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [url, livePreview, brand]);

  const close = () => {
    try {
      sessionStorage.removeItem('scl_launch_kit_url');
      sessionStorage.removeItem('scl_launch_kit_brand');
    } catch {}
    onDismiss?.();
  };

  const goBuild = (path) => {
    try {
      // Keep the URL one more step so the destination tool can pre-fill if desired
      sessionStorage.setItem('scl_first_look_url', url);
      sessionStorage.setItem('scl_first_look_brand', preview?.brand || brand || '');
      if (intelligence?.voice?.sample_headlines?.[0]) {
        sessionStorage.setItem('scl_first_look_headline', intelligence.voice.sample_headlines[0]);
      }
    } catch {}
    close();
    navigate(path);
  };

  if (!preview) return null;

  return (
    <div data-testid="first-look-celebration" role="dialog" aria-modal="true"
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(4,5,10,0.92)',
        backdropFilter: 'blur(28px) saturate(160%)',
        WebkitBackdropFilter: 'blur(28px) saturate(160%)',
        overflowY: 'auto',
        animation: 'scl-firstlook-in 0.45s cubic-bezier(0.16,1,0.3,1) both',
      }}>
      <style>{`
        @keyframes scl-firstlook-in { 0% { opacity: 0; } 100% { opacity: 1; } }
        @keyframes scl-tile-rise {
          0% { opacity: 0; transform: translateY(28px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .scl-tile-rise { animation: scl-tile-rise 0.6s cubic-bezier(0.16,1,0.3,1) both; }
      `}</style>
      <Confetti />

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(40px, 6vw, 80px) clamp(20px, 5vw, 48px)' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(36px, 5vw, 56px)' }}>
          <p className="scl-tile-rise" style={{
            fontFamily: MONO, fontSize: 11, letterSpacing: '0.24em', color: LIME,
            textTransform: 'uppercase', marginBottom: 14, animationDelay: '0ms',
          }}>Your marketing OS, calibrated</p>

          <h1 className="scl-tile-rise" style={{
            fontFamily: DISP, fontWeight: 800,
            fontSize: 'clamp(34px, 6vw, 64px)', letterSpacing: '-0.035em',
            lineHeight: 1.02, color: '#f0f0e8', marginBottom: 18, animationDelay: '80ms',
          }}>
            <span style={{ display: 'block' }}>Welcome, {preview.brand}.</span>
            <span style={{ display: 'block', color: LIME }}>Here is your kit.</span>
          </h1>

          <p className="scl-tile-rise" style={{
            fontFamily: MONO, fontSize: 13, lineHeight: 1.9, color: 'rgba(240,240,232,0.55)',
            maxWidth: 620, marginLeft: 'auto', marginRight: 'auto', animationDelay: '160ms',
          }}>
            Three pieces of content, calibrated to your brand the moment you arrived.
            Tap any one to make it real — the marketing OS will take it from there.
          </p>
        </div>

        {/* Brand Intelligence summary */}
        <div className="scl-tile-rise" data-testid="first-look-intelligence"
          style={{
            maxWidth: 880, margin: '0 auto clamp(36px, 5vw, 56px)',
            padding: '24px 28px', borderRadius: 16,
            border: `1px solid ${BORDER}`,
            background: 'linear-gradient(135deg, rgba(200,241,53,0.04), rgba(200,241,53,0.01))',
            animationDelay: '220ms',
          }}>
          <p style={{
            fontFamily: MONO, fontSize: 9, letterSpacing: '0.24em',
            color: LIME, textTransform: 'uppercase', marginBottom: 12,
          }}>Brand Intelligence</p>
          {intelLoading && !intelligence ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%', background: LIME,
                animation: 'scl-pulse 1.2s ease-in-out infinite',
              }} />
              <p style={{ fontFamily: MONO, fontSize: 13, color: 'rgba(240,240,232,0.55)', lineHeight: 1.8 }}>
                Analysing {preview.brand}\u2019s positioning, audience, and voice\u2026
              </p>
              <style>{`@keyframes scl-pulse { 0%,100% { opacity: 0.3; } 50% { opacity: 1; } }`}</style>
            </div>
          ) : intelligence ? (
            <>
              <p style={{
                fontFamily: DISP, fontWeight: 700, fontSize: 'clamp(17px, 2.2vw, 22px)',
                lineHeight: 1.45, color: '#f0f0e8', letterSpacing: '-0.012em', marginBottom: 18,
              }}>
                {intelligence.summary}
              </p>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
                gap: 14, marginTop: 18,
              }}>
                {[
                  ['Tier', intelligence.positioning?.tier],
                  ['Voice', intelligence.voice?.tone],
                  ['Audience', (() => {
                    const p = intelligence.audience?.primary;
                    if (!p) return intelligence.audience?.sophistication;
                    // Take first 3-5 words for a clean chip
                    const words = p.replace(/[,.;:].*$/, '').split(/\s+/).slice(0, 5).join(' ');
                    return words.length > 38 ? words.slice(0, 38).trim() + '\u2026' : words;
                  })()],
                  ['Visual', intelligence.visual?.sophistication],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} style={{ paddingTop: 12, borderTop: `1px solid ${BORDER}` }}>
                    <p style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '0.2em', color: 'rgba(240,240,232,0.4)', textTransform: 'uppercase', marginBottom: 5 }}>{k}</p>
                    <p style={{ fontFamily: DISP, fontWeight: 700, fontSize: 15, color: '#f0f0e8', letterSpacing: '-0.005em', textTransform: k === 'Audience' ? 'none' : 'capitalize' }}>{v}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p style={{ fontFamily: MONO, fontSize: 13, color: 'rgba(240,240,232,0.45)', lineHeight: 1.8 }}>
              We\u2019ll deepen the analysis once you start creating.
            </p>
          )}
        </div>

        {/* Tiles */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
          gap: 24,
          marginBottom: 'clamp(40px, 5vw, 64px)',
        }}>
          <div className="scl-tile-rise" style={{ animationDelay: '260ms' }}>
            <AdTile preview={preview} />
            <button onClick={() => goBuild('/dashboard/visual-ads')} data-testid="first-look-build-ad"
              style={{
                marginTop: 16, width: '100%', padding: '14px 18px', borderRadius: 12,
                background: LIME, color: BG, border: 'none', cursor: 'pointer',
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', boxShadow: '0 0 32px rgba(200,241,53,0.28)',
                transition: 'transform 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 0 48px rgba(200,241,53,0.4)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 0 32px rgba(200,241,53,0.28)'; }}>
              Make my first 3D ad &rarr;
            </button>
          </div>

          <div className="scl-tile-rise" style={{ animationDelay: '380ms' }}>
            <CarouselTile preview={preview} />
            <button onClick={() => goBuild('/dashboard/carousel-creator')} data-testid="first-look-build-carousel"
              style={{
                marginTop: 16, width: '100%', padding: '14px 18px', borderRadius: 12,
                background: 'transparent', color: '#f0f0e8',
                border: `1px solid ${BORDER}`, cursor: 'pointer',
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', transition: 'border-color 0.2s, background 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(200,241,53,0.32)'; e.currentTarget.style.background = 'rgba(200,241,53,0.04)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = 'transparent'; }}>
              Build my carousel &rarr;
            </button>
          </div>

          <div className="scl-tile-rise" style={{ animationDelay: '500ms' }}>
            <LandingTile preview={preview} />
            <button onClick={() => goBuild('/dashboard/launch-pages')} data-testid="first-look-build-landing"
              style={{
                marginTop: 16, width: '100%', padding: '14px 18px', borderRadius: 12,
                background: 'transparent', color: '#f0f0e8',
                border: `1px solid ${BORDER}`, cursor: 'pointer',
                fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                textTransform: 'uppercase', transition: 'border-color 0.2s, background 0.2s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(200,241,53,0.32)'; e.currentTarget.style.background = 'rgba(200,241,53,0.04)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = BORDER; e.currentTarget.style.background = 'transparent'; }}>
              Build my landing page &rarr;
            </button>
          </div>
        </div>

        {/* Footer skip */}
        <div className="scl-tile-rise" style={{ textAlign: 'center', animationDelay: '620ms' }}>
          <button onClick={close} data-testid="first-look-dismiss"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              fontFamily: MONO, fontSize: 11, letterSpacing: '0.18em',
              color: 'rgba(240,240,232,0.45)', textTransform: 'uppercase',
            }}>
            Open my marketing OS &rarr;
          </button>
        </div>
      </div>
    </div>
  );
};

export default FirstLookCelebration;
